
import React, { useState } from 'react';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { MODIFIED_BY_CUSTOMER, MODIFIED_BY_VENDOR } from 'constants/consignmentStates';
import styled from 'styled-components';
import { Space } from 'styles/themeSpaces';
import { SHIPPING } from 'constants/deliveryModes';
import { formatDateTime, getLocalTimezone } from 'utils/formatters';
import { getMappedDisplayState, getPillTheme } from 'utils/consignment';
import Pill from 'components/UI/Pill';
import { Main, Text } from 'styles/themeColors';
import Button from 'components/UI/Button';
import ConsignmentHistoryChange from 'data/ConsignmentHistoryChange';
import FeatureFlag from 'data/FeatureFlag';
import ConsignmentModificationEntry from 'data/ConsignmentModificationEntry';
import Modal from 'components/UI/Modal';
import OrderSnapshotPreview from 'common/OrderSnapshotPreview';
import { useAPI } from 'lib/API';
import { getConsignmentModifications } from 'requests/consignment';
import LoadingIndicator from 'components/UI/LoadingIndicator';
import { renderToast, toast } from 'components/UI/ToastNotification';


const Container = styled(FlexboxContainer)`
       flex-direction: row;
`;

// FlexboxContainer customization for each row
const Row = styled(FlexboxContainer)`
    flex-direction: row;
    padding: 4px 0;
`;

const Column = styled(FlexboxContainer)`
  margin-bottom: 0.8rem;
  flex-direction: column;
    `;


const Label = styled.span`
    min-width: 200px;
    font-size: ${Space.sp2x};
    line-height: 20px;
    color: ${Text.primary};
`;

const Value = styled.span`
    min-width: 200px;
    font-size: ${Space.sp2x};
    line-height: 20px;
    color: ${Text.primary};
`;


const PreviousValue = styled.span`
    text-decoration: line-through;
    color: ${Text.secondary}; // Lighter color for previous value
    font-size: ${Space.sp2x};

`;

const CurrentValue = styled.span`
    font-weight: bold;
    color: ${Text.primary}; // Normal color for current value
    font-size: ${Space.sp2x};

`;


interface OrderHistoryItemProps {
    isVendor: boolean;
    item: ConsignmentHistoryChange;
    featureFlags: FeatureFlag;
    consignmentModificationEntries?: ConsignmentModificationEntry[];
    isSubscription?: boolean;
}

const OrderHistoryItem: React.FC<OrderHistoryItemProps> = ({ isVendor, item, featureFlags, consignmentModificationEntries, isSubscription }) => {

    const [showModal, setShowModal] = useState(false);
    const [modificationDetails, setModificationDetails] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    const {
        toConsignmentStatus: consignmentState,
        dowJonesStatus,
        createdOn,
        message,
        deliveryTypes,
        currentCarrierName,
        currentDeliveryDate,
        currentTracking,
        currentRentEndDate,
        currentRentStartDate,
        previousCarrierName,
        previousDeliveryDate,
        previousRentEndDate,
        previousRentStartDate,
        previousTracking,
        modificationEntryId // Make sure this property exists in ConsignmentHistoryChange
    } = item || {};

    // Find corresponding modification entry if modificationEntryId exists
    const modificationEntry = modificationEntryId && consignmentModificationEntries
        ? consignmentModificationEntries.find(entry => entry.id === modificationEntryId)
        : undefined;

    // Determine if button should be shown
    const showModificationButton = modificationEntryId && modificationEntry && modificationEntry.onlyDatesChanged === false && [MODIFIED_BY_CUSTOMER, MODIFIED_BY_VENDOR].includes(consignmentState);

    const { displayState } = getMappedDisplayState(consignmentState, isVendor, dowJonesStatus, featureFlags);

    const pillTheme = getPillTheme(consignmentState, dowJonesStatus, isVendor);

    const pill = <Pill margin="0 0 0 0" theme={pillTheme} bgColor={pillTheme} size="xsPlus" padding="4px">{displayState}</Pill>;

    const renderDateValue = (previous: any, current: any, formatter = (val: any, _: any) => val) => {
        return previous && previous !== current ? (
            <>
                {previous && <PreviousValue>{formatter(previous, true)} {getLocalTimezone()}</PreviousValue>}
                <br />
                {current && <CurrentValue>{formatter(current, true)} {getLocalTimezone()}</CurrentValue>}
            </>
        ) : (
            current && <Value>{formatter(current, true)} {getLocalTimezone()}</Value>
        );
    };


    const renderValue = (previous: any, current: any) => {
        return previous && previous !== current ? (
            <>
                {previous && <PreviousValue>{previous}</PreviousValue>}
                <br />
                {current && <CurrentValue>{current}</CurrentValue>}
            </>
        ) : (
            current && <Value>{current}</Value>
        );
    };


    const consignmentModificationsReq = useAPI({
        deferFn: getConsignmentModifications,
        onResolve: result => {
            setLoading(false);
            setModificationDetails(result)
        },
        onReject: err => {
            setLoading(false);
        }
    });


    const fetchModificationDetails = async () => {
        if (modificationEntryId) {
            setLoading(true);
            consignmentModificationsReq.run(modificationEntryId);
        }
    }

    const reasonParts = message?.split?.('\n') || [];

    return <>
        <Container>
            <Row>
                <Label>Status:</Label>
                <Value>{pill}</Value>

            </Row>
        </Container>

        <Container>
            <Row gap='14rem'>
                <Row>
                    <Label>Change date:</Label>
                    <Value>{formatDateTime(createdOn, true)} {getLocalTimezone()}</Value>
                </Row>
                {!isSubscription && deliveryTypes === SHIPPING &&
                    <Row>
                        <Label>Carrier:</Label>
                        <Value>
                            {renderValue(previousCarrierName, currentCarrierName)}
                        </Value>
                    </Row>
                }
            </Row>
        </Container>

        {!isSubscription && (
        <Container>
            <Row gap='14rem'>
                <Row>
                    <Label>{deliveryTypes === SHIPPING ? 'Tracking number:' : 'Reference number:'} </Label>
                    <Value>
                        {renderValue(previousTracking, currentTracking)}
                    </Value>
                </Row>
                <Row>
                    <Label>{deliveryTypes === SHIPPING ? 'Est. ship date:' : 'Est. pickup:'}</Label>
                    <Value>
                        {renderDateValue(previousDeliveryDate, currentDeliveryDate, formatDateTime)}
                    </Value>
                </Row>

            </Row>
        </Container>
        )}

        {currentRentStartDate && currentRentEndDate &&
            <Container>
                <Row gap='14rem'>
                    <Row>
                        <Label>Rent start date:</Label>
                        <Value>
                            {renderDateValue(previousRentStartDate, currentRentStartDate, formatDateTime)}
                        </Value>
                    </Row>
                    <Row>
                        <Label>Rent end date:</Label>
                        <Value>
                            {renderDateValue(previousRentEndDate, currentRentEndDate, formatDateTime)}
                        </Value>
                    </Row>
                </Row>
            </Container>
        }

        {!isSubscription && (
        <Container>
            <Column>
                <Row>
                    <Label>Reason/Comments:</Label>
                    {
                        reasonParts.length > 0 ? (
                            <Value>
                                {reasonParts.map((part, index) => (
                                    <span key={index}>
                                        {part}
                                        {index < reasonParts.length - 1 && <br />}
                                    </span>
                                ))}
                            </Value>
                        ) : (
                            <Value></Value>
                        )
                    }
                </Row>
            </Column>
        </Container>
        )}

        <Container alignContent='center' justifyContent='center' alignItems='center'>
            <Column>
                <Row>
                    {showModificationButton && (
                        <Button
                            color={Main.black}
                            background={Main.white}
                            borderColor={Main.lightGray}
                            onClick={() => {
                                setShowModal(true);
                                fetchModificationDetails();
                            }}
                        >
                            View order changes
                        </Button>
                    )}
                </Row>
            </Column>
        </Container>

        {/* Add Modal component */}
        {showModal && (
            <Modal
                showModal={showModal}
                onClose={() => setShowModal(false)}
                title="Order Modification Details"
                showButtons={false}
                width="800px"
                left='30%'
                maxHeight="90%"
                showCloseIcon
            >
                {loading ? (
                    <LoadingIndicator />
                ) : modificationDetails ? (
                    <div>
                        <OrderSnapshotPreview
                            consignmentDetailsResponse={modificationDetails}
                            isModalView
                        />
                    </div>
                ) : (
                    <div>No modification details found</div>
                )}
            </Modal>
        )}
    </>
}

export default OrderHistoryItem;